'use client';

import { useEffect, useRef, useState, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/lib/auth/context';
import { useTwaContext } from '@/lib/twa-detection';

export interface CommandBarProps {
  open: boolean;
  onClose: () => void;
  showAdvancedWorkspaceItems?: boolean;
  showPracticeItem?: boolean;
  showAddCaseItem?: boolean;
  showBulkAddItem?: boolean;
  showPlansItem?: boolean;
}

interface Action {
  id: string;
  label: string;
  hint?: string;
  kind: 'nav' | 'action';
  run: () => void;
  icon: React.ReactNode;
}

const IconHome = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.6}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 10l8-7 8 7v10a1 1 0 01-1 1h-4v-6h-6v6H5a1 1 0 01-1-1z" />
  </svg>
);
const IconPlus = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.6}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v14M5 12h14" />
  </svg>
);
const IconPlan = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.6}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.5 7A2.5 2.5 0 016 4.5h12A2.5 2.5 0 0120.5 7v2H3.5V7z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.5 11h17v6A2.5 2.5 0 0118 19.5H6A2.5 2.5 0 013.5 17v-6z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M7 15.5h4" />
  </svg>
);
const IconPlay = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.6}>
    <circle cx="12" cy="12" r="8.5" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M10 8.8v6.4L15.2 12 10 8.8z" />
  </svg>
);
const IconDemoCases = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.6}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M7 4.5h7l3 3v12H7a2 2 0 01-2-2v-11a2 2 0 012-2z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M14 4.5v3h3M9 11h6M9 14h6M9 17h3" />
  </svg>
);
const IconBriefcase = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.6}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M10 6V5a2 2 0 012-2h0a2 2 0 012 2v1m-9 0h14a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 12h18" />
  </svg>
);
const IconUpload = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.6}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 15V4m0 0L8 8m4-4l4 4" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 15v3a2 2 0 002 2h12a2 2 0 002-2v-3" />
  </svg>
);
const IconCalendar = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.6}>
    <rect x="3.5" y="5" width="17" height="15" rx="1.5" /><path d="M8 3v4M16 3v4M3.5 10h17" />
  </svg>
);
const IconTag = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.6}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
  </svg>
);
const IconCog = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.6}>
    <circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09a1.65 1.65 0 00-1-1.51 1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09a1.65 1.65 0 001.51-1 1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33h.01a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82v.01a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
  </svg>
);
const IconDiscover = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.6}>
    <circle cx="10.5" cy="10.5" r="6.5" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M20 20l-4.2-4.2" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 7.5v6M7.5 10.5h6" />
  </svg>
);
const IconLogout = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.6}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
  </svg>
);
const IconArrow = (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
  </svg>
);

export default function CommandBar({
  open,
  onClose,
  showAdvancedWorkspaceItems = false,
  showPracticeItem = false,
  showAddCaseItem = true,
  showBulkAddItem = false,
  showPlansItem = false,
}: CommandBarProps) {
  const router = useRouter();
  const { signOut } = useAuth();
  const isTwa = useTwaContext();
  const [query, setQuery] = useState('');
  const [index, setIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const actions: Action[] = useMemo(() => {
    const workspaceActions: Action[] = [
      { id: 'go-dashboard', kind: 'nav', label: 'Go to Dashboard', hint: 'Overview of active cases', icon: IconHome, run: () => router.push('/dashboard') },
      { id: 'go-demo-cases', kind: 'nav', label: 'Open Demo Cases', hint: 'Read-only sample matters', icon: IconDemoCases, run: () => router.push('/demo-cases') },
      { id: 'go-demo-video', kind: 'nav', label: 'Watch Demo Video', hint: 'Dashboard walkthrough', icon: IconPlay, run: () => router.push('/dashboard/watch-demo-video') },
    ];

    if (showAddCaseItem) {
      workspaceActions.push(
        { id: 'go-add-case', kind: 'nav', label: 'Add a Case', hint: 'Track a new matter', icon: IconPlus, run: () => router.push('/add-case') },
      );
      if (showBulkAddItem) {
        workspaceActions.push(
          { id: 'go-bulk-add', kind: 'nav', label: 'Bulk Add Cases', hint: 'Import a CNR list', icon: IconUpload, run: () => router.push('/bulk-add-cases') },
        );
      }
    }
    if (showPlansItem && !isTwa) {
      workspaceActions.push(
        { id: 'go-pricing', kind: 'nav', label: 'See Plans', hint: 'Choose a tracking plan', icon: IconPlan, run: () => router.push('/pricing') },
      );
    }

    if (showAdvancedWorkspaceItems) {
      workspaceActions.push(
        { id: 'go-calendar', kind: 'nav', label: 'Open Calendar', hint: 'Upcoming hearings', icon: IconCalendar, run: () => router.push('/calendar') },
        { id: 'go-labels', kind: 'nav', label: 'Manage Labels', hint: 'Organise matters by tag', icon: IconTag, run: () => router.push('/settings/labels') },
      );
    }
    if (showPracticeItem) {
      workspaceActions.push(
        { id: 'go-practice', kind: 'nav', label: 'Open Practice', hint: 'Tasks, clients, money, documents', icon: IconBriefcase, run: () => router.push('/practice') },
      );
    }
    workspaceActions.push(
      { id: 'go-discovery', kind: 'nav', label: 'Open Discovery', hint: 'Keyword watches and new-case alerts', icon: IconDiscover, run: () => router.push('/discovery') },
    );

    return [
      ...workspaceActions,
      { id: 'go-settings', kind: 'nav', label: 'Settings', hint: 'Profile, notifications, billing', icon: IconCog, run: () => router.push('/settings') },
      { id: 'sign-out', kind: 'action', label: 'Sign out', icon: IconLogout, run: async () => { await signOut(); window.location.href = '/'; } },
    ];
  }, [router, signOut, showAddCaseItem, showAdvancedWorkspaceItems, showBulkAddItem, showPracticeItem, showPlansItem, isTwa]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return actions;
    return actions.filter((a) => a.label.toLowerCase().includes(q) || (a.hint?.toLowerCase().includes(q) ?? false));
  }, [actions, query]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setIndex(0);
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  useEffect(() => { setIndex(0); }, [query]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); }
      if (e.key === 'ArrowDown') { e.preventDefault(); setIndex((i) => Math.min(filtered.length - 1, i + 1)); }
      if (e.key === 'ArrowUp') { e.preventDefault(); setIndex((i) => Math.max(0, i - 1)); }
      if (e.key === 'Enter') {
        e.preventDefault();
        const chosen = filtered[index];
        if (chosen) { chosen.run(); onClose(); }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, filtered, index, onClose]);

  if (!open) return null;

  const groups = {
    nav: filtered.filter((a) => a.kind === 'nav'),
    action: filtered.filter((a) => a.kind === 'action'),
  };

  const renderGroup = (title: string, items: Action[], offset: number) => {
    if (items.length === 0) return null;
    return (
      <div className="cmd-group">
        <div className="cmd-group-title eyebrow">{title}</div>
        {items.map((a, i) => {
          const abs = offset + i;
          const on = abs === index;
          return (
            <button
              key={a.id}
              type="button"
              className={`cmd-item ${on ? 'on' : ''}`}
              onMouseEnter={() => setIndex(abs)}
              onClick={() => { a.run(); onClose(); }}
            >
              <span className="text-gray-500 flex-shrink-0">{a.icon}</span>
              <span className="cmd-label grow">{a.label}</span>
              {a.hint && <span className="text-[11px] text-gray-500 hidden sm:inline">{a.hint}</span>}
              <span className="text-primary flex-shrink-0">{IconArrow}</span>
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <div
      className="cmd-overlay animate-fade-in"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
    >
      <div className="cmd-panel animate-scale-in" onClick={(e) => e.stopPropagation()}>
        <div className="cmd-input-row">
          <svg className="w-4 h-4 text-gray-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.6}>
            <circle cx="11" cy="11" r="6.5" /><path d="M20 20l-3.8-3.8" />
          </svg>
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="cmd-input"
            placeholder="Ask or jump to…"
          />
          <kbd className="kbd-x">esc</kbd>
        </div>
        <div className="cmd-body">
          {filtered.length === 0 ? (
            <div className="cmd-empty text-gray-500">No matches for &ldquo;{query}&rdquo;</div>
          ) : (
            <>
              {renderGroup('Navigate', groups.nav, 0)}
              {renderGroup('Account', groups.action, groups.nav.length)}
            </>
          )}
        </div>
        <div className="cmd-foot text-[11px] text-gray-500">
          <span><kbd>↑↓</kbd>navigate</span>
          <span><kbd>↵</kbd>select</span>
          <span><kbd>esc</kbd>close</span>
        </div>
      </div>
    </div>
  );
}
